import { useMemo, useState } from 'react';
import { Activity as ActivityIcon, Search } from 'lucide-react';
import { Card, CardBody } from '@/shared/components/ui/Card';
import { Button } from '@/shared/components/ui/Button';
import { PageHeader } from '@/shared/components/ui/PageHeader';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { useUser } from '@/shared/hooks/useAuth';
import { ActivityTimeline } from '../components/ActivityTimeline';
import { useActivityFeed } from '../hooks/useActivity';
import { useUserOptions } from '@/shared/hooks/useUserOptions';
import { ACTION_FILTER_OPTIONS, resolveAction } from '../constants';
import type { ActivityFilters, ActivityItem } from '../types';

const PAGE = 50;
const MAX = 200; // backend hard cap on /api/activity

const GROUP_ORDER = ['Today', 'Yesterday', 'This Week', 'Older'] as const;
type GroupLabel = (typeof GROUP_ORDER)[number];

function recencyBucket(iso: string): GroupLabel {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return 'Older';
  const diff = Date.now() - t;
  const day = 86_400_000;
  if (diff < day) return 'Today';
  if (diff < 2 * day) return 'Yesterday';
  if (diff < 7 * day) return 'This Week';
  return 'Older';
}

function groupByRecency(items: ActivityItem[]): { label: GroupLabel; items: ActivityItem[] }[] {
  const map = new Map<GroupLabel, ActivityItem[]>();
  for (const i of items) {
    const b = recencyBucket(i.created_at);
    (map.get(b) ?? map.set(b, []).get(b)!).push(i);
  }
  return GROUP_ORDER.filter((g) => map.has(g)).map((g) => ({ label: g, items: map.get(g)! }));
}

export function ActivityPage() {
  const user = useUser();
  const isPrivileged = ['admin', 'superadmin'].includes(user?.role ?? '');

  const [action, setAction] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [userId, setUserId] = useState('');
  const [search, setSearch] = useState('');
  const [limit, setLimit] = useState(PAGE);
  // Sign-ins flood the feed and bury the business actions, so they're hidden by default.
  const [hideLogins, setHideLogins] = useState(true);

  const filters = useMemo<ActivityFilters>(
    () => ({ action, from, to, userId: isPrivileged ? userId : undefined, exclude: hideLogins ? ['login'] : undefined, limit, offset: 0 }),
    [action, from, to, userId, isPrivileged, hideLogins, limit],
  );

  const { data: items = [], isLoading, isError, isFetching } = useActivityFeed(filters);
  const { data: users = [] } = useUserOptions(isPrivileged);

  // Client-side search over actor / detail / resolved action label.
  const searched = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((i) =>
      [i.actor, i.detail, resolveAction(i.action).label, i.entity_type].some((f) =>
        (f ?? '').toLowerCase().includes(q),
      ),
    );
  }, [items, search]);

  // Group by recency (Today / Yesterday / This Week / Older) from real created_at.
  const groups = useMemo(() => groupByRecency(searched), [searched]);

  // Filter out sales-specific activities for non-sales departments
  const filteredActionOptions = useMemo(() => {
    if (user?.role === 'superadmin' || user?.department === 'sales') return ACTION_FILTER_OPTIONS;
    const salesActions = new Set(['lead_status', 'follow_up_set', 'call_logged', 'deal_create', 'deal_stage', 'deal_update', 'deal_delete', 'scan_start', 'scan_done']);
    return ACTION_FILTER_OPTIONS.filter((opt) => !salesActions.has(opt.value));
  }, [user?.role, user?.department]);

  const ownerOptions = useMemo(() => {
    const isSuperOrAdmin = user?.role === 'superadmin' || user?.role === 'admin';
    const validUsers = isSuperOrAdmin
      ? users
      : users.filter((u) => u.department === user?.department);
    const hasMe = validUsers.some((u) => String(u.id) === String(user?.id));
    const combined = hasMe || !user
      ? validUsers
      : [{ id: user.id, display_name: `${user.displayName || user.username || 'My Activity'} (You)`, department: user.department }, ...validUsers];

    return [
      { value: '', label: 'All people' },
      ...combined.map((u) => ({
        value: u.id,
        label: String(u.id) === String(user?.id) ? `${u.display_name} (You)` : u.display_name,
      })),
    ];
  }, [users, user?.role, user?.department, user?.id, user?.displayName, user?.username]);

  const canLoadMore = items.length >= limit && limit < MAX;

  const resetPaging = <T,>(setter: (v: T) => void) => (v: T) => {
    setLimit(PAGE);
    setter(v);
  };

  return (
    <div className="mx-auto flex h-full w-full max-w-[1580px] flex-col gap-4 pb-6">
      <PageHeader
        icon={ActivityIcon}
        eyebrow="Oversight"
        title="Activity"
        description="Who did what, and when — quotes, orders, invoices, dispatch, contacts and samples."
      />

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-[200px] flex-1">
          <Input
            name="activity-search"
            placeholder="Search activity, person, entity…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            leftIcon={<Search className="h-4 w-4" />}
          />
        </div>
        <div className="w-52">
          <Select
            name="activity-action"
            value={action}
            onChange={(e) => resetPaging(setAction)(e.target.value)}
            options={filteredActionOptions}
          />
        </div>
        {isPrivileged && (
          <div className="w-48">
            <Select
              name="activity-owner"
              value={userId}
              onChange={(e) => resetPaging(setUserId)(e.target.value)}
              options={ownerOptions}
            />
          </div>
        )}
        <div className="w-40">
          <Input name="activity-from" type="date" value={from} onChange={(e) => resetPaging(setFrom)(e.target.value)} />
        </div>
        <div className="w-40">
          <Input name="activity-to" type="date" value={to} onChange={(e) => resetPaging(setTo)(e.target.value)} />
        </div>
        <label className="inline-flex cursor-pointer select-none items-center gap-2 text-body-sm text-on-surface-variant">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-outline accent-primary"
            checked={hideLogins}
            disabled={!!action}
            onChange={(e) => resetPaging(setHideLogins)(e.target.checked)}
          />
          Hide sign-ins
        </label>
      </div>

      <Card className="flex-1 overflow-hidden">
        <CardBody className="h-full overflow-y-auto">
          {isLoading || isError || groups.length === 0 ? (
            <ActivityTimeline items={[]} isLoading={isLoading} isError={isError} showActor />
          ) : (
            <div className="flex flex-col gap-6">
              {groups.map((group) => (
                <section key={group.label} className="flex flex-col gap-3">
                  <div className="sticky top-0 z-10 -mx-1 bg-surface/85 px-1 py-1 backdrop-blur-sm">
                    <span className="text-label-caps uppercase text-on-surface-variant">{group.label}</span>
                  </div>
                  <ActivityTimeline items={group.items} showActor />
                </section>
              ))}
            </div>
          )}
          {canLoadMore && (
            <div className="mt-4 flex justify-center">
              <Button variant="outline" loading={isFetching} onClick={() => setLimit((l) => Math.min(l + PAGE, MAX))}>
                Load more
              </Button>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
